import { useState, useEffect } from "react";
import styles from "./UnsubscribeFlowModal.module.scss";

interface UnsubscribeFlowModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    feedback: { question: string; answer: string }[],
  ) => Promise<void>;
  isLoading: boolean;
}

const QUESTION_1 =
  "Что именно не понравилось и почему решили отменить подписку?";
const QUESTION_2 =
  "А что вам нравилось и что хорошего было в наших сервисах лично для вас?";

const MIN_ANSWER_LENGTH = 20;
const HINT = `Не менее ${MIN_ANSWER_LENGTH} символов`;

export function UnsubscribeFlowModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}: UnsubscribeFlowModalProps) {
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [showReasonError, setShowReasonError] = useState(false);
  const [showCommentError, setShowCommentError] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setStep(1);
        setReason("");
        setComment("");
        setShowReasonError(false);
        setShowCommentError(false);
      }, 300);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleNext = () => {
    setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      onClose();
    }
  };

  const handleFinish = async () => {
    if (comment.length < MIN_ANSWER_LENGTH) {
      setShowCommentError(true);
      return;
    }
    setShowCommentError(false);

    const feedback = [
      {
        question: QUESTION_1,
        answer: reason,
      },
      {
        question: QUESTION_2,
        answer: comment,
      },
    ];
    try {
      await onConfirm(feedback);
      setStep(4);
    } catch (error) {
      console.error("Unsubscribe failed:", error);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <>
            <h2 className={styles.title}>
              Если вы отмените подписку, вы больше не сможете:
            </h2>
            <ul className={styles.text}>
              <li>- Получать актуальные данные по вашему кабинету</li>
              <li>- Оперативно отслеживать Заказы / ДРР / CTR / Конверсии</li>
            </ul>
            <p className={`${styles.text} ${styles.highlight}`}>
              Вы уверены, что хотите отменить подписку?
            </p>
            <p className={styles.text}>
              Перед отменой зададим два коротких вопроса.
            </p>
            <div className={styles.buttons}>
              <button
                onClick={handleNext}
                className={`${styles.button} ${styles.confirmButton}`}
              >
                Продолжить отмену
              </button>
              <button
                onClick={onClose}
                className={`${styles.button} ${styles.cancelButton}`}
              >
                Назад
              </button>
            </div>
          </>
        );
      case 2:
        return (
          <>
            <div className={styles.stepBadge}>Вопрос 1 из 2</div>
            <h2 className={styles.title}>Запрос на отмену подписки получен</h2>
            <div className={styles.highlight}>
              ❗️Но ПОДПИСКА пока НЕ ОТМЕНЕНА️ ❗️
            </div>
            <p className={styles.text}>Расскажите, пожалуйста о своём опыте:</p>
            <p className={`${styles.text} ${styles.question}`}>{QUESTION_1}</p>
            <p className={styles.text}>
              Посоветуйте, что нам улучшить, чтобы быть действительно ценными
              для вас.
              <br />
              Мы внимательно изучаем все отклики - это основа для развития
              проекта. 💪
            </p>
            <div className={styles.textareaWrapper}>
              <textarea
                className={styles.textarea}
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  if (showReasonError) {
                    setShowReasonError(false);
                  }
                }}
                placeholder="Ваш комментарий..."
              />
              {showReasonError ? (
                <div className={styles.inputError}>
                  Комментарий должен быть не менее {MIN_ANSWER_LENGTH} символов
                </div>
              ) : (
                <div className={styles.inputHint}>{HINT}</div>
              )}
            </div>
            <div className={styles.buttons}>
              <button
                onClick={() => {
                  if (reason.length < MIN_ANSWER_LENGTH) {
                    setShowReasonError(true);
                  } else {
                    handleNext();
                  }
                }}
                className={`${styles.button} ${styles.confirmButton}`}
              >
                Далее — остался 1 вопрос
              </button>
              <button
                onClick={handleBack}
                className={`${styles.button} ${styles.cancelButton}`}
              >
                Назад
              </button>
            </div>
          </>
        );
      case 3:
        return (
          <>
            <div className={styles.stepBadge}>Вопрос 2 из 2 — последний</div>
            <h2 className={styles.title}>Спасибо за ответ!</h2>
            <div className={styles.highlight}>
              ❗️Но ПОДПИСКА все еще НЕ ОТМЕНЕНА❗️
            </div>
            <p className={`${styles.text} ${styles.question}`}>{QUESTION_2}</p>
            <p className={styles.text}>
              ⚠️ Подписка пока активна. Напишите в службу заботы{" "}
              <a
                className={styles.link}
                href="https://t.me/selleralarm_support_bot"
              >
                @selleralarm_support_bot
              </a>
              , если остались вопросы или что-то не получается, мы разберёмся
              лично и быстро.
            </p>
            <div className={styles.textareaWrapper}>
              <textarea
                className={styles.textarea}
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                  if (showCommentError) {
                    setShowCommentError(false);
                  }
                }}
                placeholder="Ваш комментарий..."
              />
              {showCommentError ? (
                <div className={styles.inputError}>
                  Комментарий должен быть не менее {MIN_ANSWER_LENGTH} символов
                </div>
              ) : (
                <div className={styles.inputHint}>{HINT}</div>
              )}
            </div>
            <div className={styles.buttons}>
              <button
                onClick={handleFinish}
                className={`${styles.button} ${styles.confirmButton}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className={styles.spinner} />
                ) : (
                  "Отменить подписку"
                )}
              </button>
              <button
                onClick={handleBack}
                className={`${styles.button} ${styles.cancelButton}`}
                disabled={isLoading}
              >
                Назад
              </button>
            </div>
          </>
        );
      case 4:
        return (
          <>
            <h2 className={styles.title}>Ваша подписка отменена!</h2>
            <p className={styles.text}>
              Были рады работать с вами!
              {"\n"}
              Остаемся на связи!
            </p>
            <div className={styles.buttons}>
              <button
                onClick={onClose}
                className={`${styles.button} ${styles.cancelButton}`}
              >
                Закрыть
              </button>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  // Клик по фону на шагах с вопросами не закрывает окно: там уже введённые
  // ответы, и случайный тап мимо стирал их вместе со всем прогрессом.
  const handleOverlayClick = () => {
    if (isLoading || step === 2 || step === 3) {
      return;
    }
    onClose();
  };

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modalWrapper} onClick={(e) => e.stopPropagation()}>
        {renderStep()}
      </div>
    </div>
  );
}
